import React, { useCallback, useEffect, useState } from "react";
import {
  Box, Container, Typography, IconButton, CircularProgress, Chip, TextField,
  MenuItem,
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Tooltip,
} from "@mui/material";
import { Edit, Close, Delete } from "@mui/icons-material";
import HeaderInternal from "../components/Header/HeaderInternal";
import { FooterInternal } from "../components/Footer";
import { FaqButton } from "../components/FaqButton";
import AddButton from "../components/AddButton";
import ClearFiltersButton from "../components/ClearFiltersButton";
import ResultCount from "../components/ResultCount";
import StandardPagination, { ITENS_POR_PAGINA_PADRAO } from "../components/Pagination/StandardPagination";
import ConfirmModal from "../components/modals/ConfirmModal";
import SavingOverlay from "../components/SavingOverlay";
import { Toast } from "../components/Toast";
import { useToast } from "../hooks/useToast";
import { useNavigation } from "../contexts/RouterContext";
import { useAuth } from "../contexts/AuthContext";
import { colors, typography, pageHeader } from "../theme/designSystem";
import { clinicPeriodsService, ClinicPeriodRow } from "../services/clinicPeriodsService";
import { therapyPlanErrorMessage } from "../services/patientTherapyPlanService";

/**
 * Períodos de planos de ação (RN-541e): a régua de tempo da clínica — "2026 - 1º Semestre" e afins —
 * que fica disponível a todos os pacientes da entidade.
 *
 * Existe para acabar com o texto livre: antes, cada profissional digitava o nome do período e a
 * base acumulou quatro convenções diferentes na mesma coluna. Quem precisar sair do padrão
 * continua criando um ciclo próprio dentro do cadastro do paciente.
 */
const SITUACAO: Record<string, { label: string; bg: string; color: string }> = {
  vigente: { label: "Vigente", bg: "#d4edda", color: "#155724" },
  futuro: { label: "Futuro", bg: "#e3f2fd", color: "#0d47a1" },
  encerrado: { label: "Encerrado", bg: "#e9ecef", color: "#495057" },
};

const MAX_TITULO = 255;

const formatarData = (iso: string | null): string => {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
};

const ClinicPeriods: React.FC = () => {
  const { goToDashboard } = useNavigation();
  const { user } = useAuth();
  const { toast, showToast, hideToast } = useToast();

  const [items, setItems] = useState<ClinicPeriodRow[]>([]);
  const [search, setSearch] = useState("");
  const [situation, setSituation] = useState<"todas" | "vigente" | "futuro" | "encerrado">("todas");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(ITENS_POR_PAGINA_PADRAO);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [editing, setEditing] = useState<ClinicPeriodRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [title, setTitle] = useState("");
  const [initialDate, setInitialDate] = useState("");
  const [finalDate, setFinalDate] = useState("");
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [deleteTarget, setDeleteTarget] = useState<ClinicPeriodRow | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setItems(await clinicPeriodsService.getAll());
    } catch {
      setError("Não foi possível carregar os períodos.");
    } finally {
      setLoading(false);
    }
  }, []);

  /** Minusculas e sem acento: quem busca "1o semestre" espera achar "1º Semestre". */
  const normalize = (v: string) =>
    v.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");

  // As datas do filtro recortam por INTERSECAO, nao por igualdade: quem procura
  // "periodos que tocam agosto" quer os que comecaram antes e terminam depois.
  const filtered = items.filter((row) => {
    if (situation !== "todas" && row.situation !== situation) {
      return false;
    }
    if (search.trim() && !normalize(row.title || "").includes(normalize(search))) {
      return false;
    }
    if (fromDate && row.finalDate && row.finalDate.slice(0, 10) < fromDate) {
      return false;
    }
    if (toDate && row.initialDate && row.initialDate.slice(0, 10) > toDate) {
      return false;
    }
    return true;
  });
  const totalPages = Math.max(1, Math.ceil(filtered.length / itemsPerPage));
  const paged = filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  useEffect(() => { setCurrentPage(1); }, [search, fromDate, toDate]);

  const clearFilters = () => {
    setSearch("");
    setSituation("todas");
    setFromDate("");
    setToDate("");
  };

  const hasFilterChanges =
    search !== "" || situation !== "todas" || fromDate !== "" || toDate !== "";

  useEffect(() => { load(); }, [load]);

  const abrirNovo = () => {
    const ano = new Date().getFullYear();
    setCreating(true);
    setEditing(null);
    setTitle("");
    setInitialDate(`${ano}-01-01`);
    setFinalDate(`${ano}-06-30`);
    setFormError(null);
  };

  const abrirEdicao = (row: ClinicPeriodRow) => {
    setCreating(false);
    setEditing(row);
    setTitle(row.title);
    setInitialDate(row.initialDate ?? "");
    setFinalDate(row.finalDate ?? "");
    setFormError(null);
  };

  const fechar = () => {
    setCreating(false);
    setEditing(null);
    setFormError(null);
  };

  const salvar = async () => {
    setFormError(null);
    if (!title.trim()) {
      setFormError("Informe o nome do período.");
      return;
    }
    if (!initialDate || !finalDate) {
      setFormError("Informe a data inicial e a final.");
      return;
    }
    if (finalDate < initialDate) {
      setFormError("A data final não pode ser anterior à inicial.");
      return;
    }

    setSaving(true);
    try {
      await clinicPeriodsService.save({
        id: editing?.id,
        title: title.trim(),
        initialDate: `${initialDate}T00:00:00Z`,
        finalDate: `${finalDate}T00:00:00Z`,
      });
      showToast(editing ? "Período atualizado." : "Período criado.", "success");
      fechar();
      await load();
    } catch (e) {
      setFormError(therapyPlanErrorMessage(e, "Não foi possível salvar o período."));
    } finally {
      setSaving(false);
    }
  };

  const excluir = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await clinicPeriodsService.remove(deleteTarget.id);
      setDeleteTarget(null);
      showToast("Período removido.", "success");
      await load();
    } catch (e) {
      setDeleteTarget(null);
      showToast(therapyPlanErrorMessage(e, "Não foi possível remover o período."), "error");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <HeaderInternal
        showCTAButton={false}
        className="login-header"
        isLoggedIn={true}
        userEmail={user?.email || ""}
        userProfile={user?.role || ""}
        clinicName={user?.clinicName || "Clínica"}
        notificationCount={0}
        onRevalidateLogin={() => {}}
        onNotificationClick={() => {}}
        onUserClick={() => {}}
        onLogoClick={goToDashboard}
      />

      <Box component="main" sx={{ padding: "1rem", minHeight: "calc(100vh - 120px)", background: colors.background, marginTop: "85px", flex: 1 }}>
        <Container maxWidth={false} disableGutters sx={pageHeader.container}>
          <Box className="c4u-page-header c4u-page-header--in-panel">
            <Box className="c4u-page-header__texts">
              <Typography variant="h4" className="c4u-page-header__title">Períodos de planos de ação
              </Typography>
              <Typography variant="body2" className="c4u-page-header__subtitle">Régua de tempo disponível a todos os pacientes ao traçar um objetivo do plano de ação.
              </Typography>
            </Box>
            <Box className="c4u-page-header__actions">
              <FaqButton />
            </Box>
          </Box>

          <div className="c4u-panel">
          {error && (
            <Typography sx={{ color: colors.error, fontSize: typography.fontSize.sm, mb: 2 }}>{error}</Typography>
          )}

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress sx={{ color: colors.primary }} />
            </Box>
          ) : items.length === 0 ? (
            <Typography sx={{ fontSize: typography.fontSize.sm, color: colors.textSecondary, py: 4, textAlign: "center" }}>
              Nenhum período cadastrado. Sem eles, cada profissional cria o ciclo direto no cadastro do paciente.
            </Typography>
          ) : (
            <>
              <div className="c4u-filters__row">
                <div className="c4u-filters__fields">
                  <TextField
                    size="small"
                    label="Busca por nome"
                    placeholder="Buscar período"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    inputProps={{ maxLength: MAX_TITULO }}
                  />
                  <TextField
                    select
                    size="small"
                    label="Situação"
                    value={situation}
                    onChange={(e) =>
                      setSituation(
                        e.target.value as "todas" | "vigente" | "futuro" | "encerrado",
                      )
                    }
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  >
                    <MenuItem value="todas">Todas</MenuItem>
                    <MenuItem value="vigente">Vigente</MenuItem>
                    <MenuItem value="futuro">Futuro</MenuItem>
                    <MenuItem value="encerrado">Encerrado</MenuItem>
                  </TextField>
                  <TextField
                    size="small"
                    type="date"
                    label="Início a partir de"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                  <TextField
                    size="small"
                    type="date"
                    label="Fim até"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                </div>

                <div className="c4u-filters__actions">
                  <ClearFiltersButton onClick={clearFilters} disabled={!hasFilterChanges} />
                  <AddButton onClick={abrirNovo} title="Novo período" />
                </div>
              </div>

              <ResultCount shown={filtered.length} total={items.length} />
              {/* Cabeçalho: toda lista tem um (decisão do Hilton, 02/09). Cada dado na sua
                  coluna — nada de duas linhas empilhando informações diferentes. */}
              <Box className="c4u-table__header">
                <Box className="admin-plans-header-cell" sx={{ flex: "2 1 200px", minWidth: 0 }}>Período</Box>
                <Box className="admin-plans-header-cell" sx={{ flex: "0 0 110px" }}>Situação</Box>
                <Box className="admin-plans-header-cell c4u-col--md" sx={{ flex: "0 0 100px" }}>Início</Box>
                <Box className="admin-plans-header-cell c4u-col--md" sx={{ flex: "0 0 100px" }}>Fim</Box>
                <Box className="admin-plans-header-cell c4u-col--lg" sx={{ flex: "0 0 90px" }}>Em uso</Box>
                <Box className="admin-plans-header-cell" sx={{ flex: "0 0 90px" }}>Ações</Box>
              </Box>

              {paged.map(row => {
                const s = SITUACAO[row.situation] ?? SITUACAO.vigente;
                return (
                  <Box
                    key={row.id}
                    className="c4u-table__row"
                  >
                    <Typography data-label="Período" className="admin-plans-cell" sx={{ flex: "2 1 200px", minWidth: 0, fontWeight: 600, fontSize: "0.875rem", color: colors.text }}>
                      {row.title}
                    </Typography>
                    <Box className="admin-plans-cell" data-label="Situação" sx={{ flex: "0 0 110px" }}>
                      <Chip
                        label={s.label}
                        size="small"
                        sx={{ height: 22, fontSize: "0.72rem", backgroundColor: s.bg, color: s.color }}
                      />
                    </Box>
                    <Typography className="admin-plans-cell c4u-col--md" data-label="Início" sx={{ flex: "0 0 100px", fontSize: "0.85rem", color: colors.textSecondary }}>
                      {formatarData(row.initialDate)}
                    </Typography>
                    <Typography className="admin-plans-cell c4u-col--md" data-label="Fim" sx={{ flex: "0 0 100px", fontSize: "0.85rem", color: colors.textSecondary }}>
                      {formatarData(row.finalDate)}
                    </Typography>
                    <Typography className="admin-plans-cell c4u-col--lg" data-label="Em uso" sx={{ flex: "0 0 90px", fontSize: "0.85rem", color: colors.textSecondary }}>
                      {row.goalsCount > 0 ? `${row.goalsCount} objetivo(s)` : "—"}
                    </Typography>
                    <Box className="admin-plans-cell admin-plans-actions" data-label="Ações" sx={{ flex: "0 0 90px", display: "flex", gap: 0.5, justifyContent: "flex-end" }}>
                      <Tooltip title="Editar período">
                        <IconButton className="c4u-row-action" size="small" onClick={(e) => { e.stopPropagation(); abrirEdicao(row); }} sx={{ backgroundColor: colors.primary, color: "white", "&:hover": { backgroundColor: colors.primaryHover } }}>
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={row.goalsCount > 0 ? "Período em uso não pode ser removido" : "Remover período"}>
                        <span>
                          <IconButton
                            className="c4u-row-action"
                            size="small"
                            onClick={(e) => { e.stopPropagation(); setDeleteTarget(row); }}
                            disabled={row.goalsCount > 0}
                            sx={{ backgroundColor: colors.error, color: "white", "&:hover": { backgroundColor: "#c82333" } }}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </Box>
                );
              })}

              {filtered.length > 0 && (
                <StandardPagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  itemsPerPage={itemsPerPage}
                  totalItems={filtered.length}
                  onPageChange={setCurrentPage}
                  onItemsPerPageChange={(n) => { setItemsPerPage(n); setCurrentPage(1); }}
                />
              )}
            </>
          )}
          </div>
        </Container>
      </Box>

      <Dialog
        open={creating || editing !== null}
        onClose={fechar}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "12px" } }}
      >
        <DialogTitle sx={{ backgroundColor: colors.primary, color: colors.white, display: "flex", justifyContent: "space-between", alignItems: "center", padding: "1.25rem" }}>
          <Typography component="h3" sx={{ fontSize: "1.1rem", fontWeight: typography.fontWeight.semibold }}>
            {editing ? "Editar período" : "Novo período"}
          </Typography>
          <IconButton onClick={fechar} sx={{ color: colors.white, padding: "0.25rem" }}>
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ padding: "1.25rem !important", paddingTop: "1.5rem !important" }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              label="Nome do período"
              placeholder="2026 - 1º Semestre"
              value={title}
              onChange={e => setTitle(e.target.value)}
              fullWidth
              size="small"
              inputProps={{ maxLength: MAX_TITULO }}
              InputLabelProps={{ shrink: true }}
            />
            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
              <TextField
                label="Data inicial"
                type="date"
                value={initialDate}
                onChange={e => setInitialDate(e.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
                sx={{ flex: "1 1 140px" }}
              />
              <TextField
                label="Data final"
                type="date"
                value={finalDate}
                onChange={e => setFinalDate(e.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
                sx={{ flex: "1 1 140px" }}
              />
            </Box>
            {formError && (
              <Typography sx={{ color: colors.error, fontSize: typography.fontSize.sm }}>{formError}</Typography>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ padding: "1rem 1.25rem" }}>
          <Button onClick={fechar} variant="outlined" sx={{ textTransform: "none" }}>Cancelar</Button>
          <Button onClick={salvar} variant="contained" disabled={saving} sx={{ textTransform: "none" }}>
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmModal
        isOpen={deleteTarget !== null}
        title="Remover período"
        message={`O período "${deleteTarget?.title}" deixa de aparecer na lista de escolha dos objetivos. Períodos já usados por algum objetivo não podem ser removidos.`}
        confirmButtonText="Remover"
        cancelButtonText="Cancelar"
        type="danger"
        onConfirm={excluir}
        onCancel={() => setDeleteTarget(null)}
        isProcessing={deleting}
      />

      <SavingOverlay open={saving} message="Salvando período..." />
      <Toast isVisible={toast.isVisible} message={toast.message} type={toast.type} onClose={hideToast} />
      <FooterInternal className="login-footer-component" />
    </Box>
  );
};

export default ClinicPeriods;
